import React, { useContext } from "react";
import { ChatbotContext } from "@/store/context/ChatbotContext.tsx";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/shadcn/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/shadcn/form";
import { Textarea } from "@/components/shadcn/textarea";
import { Switch } from "@/components/shadcn/switch";

import { useTranslation } from "react-i18next";
import { saveSettings } from "@/store/chatbot.ts";
import { useParams } from "react-router-dom";
import {Icon} from "@/components/Icon.tsx";
import { Input } from "@/components/shadcn/input.tsx";

const templateDefault =
  "After the second message assistant must politely ask a user to type these data: email, name. The text asking for data should be highlighted in bold.";
const templateResponseDefault =
  "Thank for the data provided, say that the manager will contact you shortly";

const FormSchema = z.object({
  enableCapture: z.boolean(),
  instruction: z
    .string()
    .min(10, { message: "Must be at least 10 characters." })
    .max(1000, { message: "must be shorter than 1000 characters" })
    .or(z.literal("")),
  response: z
    .string()
    .min(10, { message: "Must be at least 10 characters." })
    .or(z.literal("")),
  fields: z.array(
    z.object({
      title: z.string(),
      description: z
        .string()
        .max(1000, { message: "Must be shorter than 100 characters" }),
    }),
  )
});

export default function LeadsSettings() {
  const { chatbotID } = useParams();
  const { t } = useTranslation();
  const { chatSettings, viewChatbot, invalidateChatbot, setSettings } =
    useContext(ChatbotContext);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      enableCapture: chatSettings?.leads.enableCapture,
      instruction: chatSettings?.leads.instruction,
      response: chatSettings?.leads.response,
      fields: chatSettings?.leads.fields,
    },
  });

  const fieldsList = useFieldArray({
    name: "fields",
    control: form.control,
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    let set = Object.assign({}, chatSettings);
    set.leads = { ...set.leads, ...data };
    console.log(set.leads);
    saveSettings(chatbotID, set, invalidateChatbot);
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full space-y-6 pt-6"
      >
        <h3 className="inline-block text-lg font-medium">
          <Icon
            className="inline-block mr-1"
            icon="fluent:people-team-20-regular"
            width={20}
          />
          {t("settings")}
        </h3>

        <FormField
          control={form.control}
          name="enableCapture"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm mt-6">
              <div className="space-y-0.5">
                <FormLabel className="cursor-pointer">
                  {t("leads.capture_lead_data")}
                </FormLabel>
                <FormDescription>
                  {t("leads.capture_lead_data.description")}
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="instruction"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>{t("leads.instruction")}</FormLabel>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    form.setValue("instruction", templateDefault);
                  }}
                >
                  {t("use_template")}
                </Button>
              </div>
              <FormControl>
                <Textarea placeholder="" rows={6} {...field} />
              </FormControl>
              <FormDescription>
                {t("leads.instruction.description")}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="response"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>{t("leads.response")}</FormLabel>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    form.setValue("response", templateResponseDefault);
                  }}
                >
                  {t("use_template")}
                </Button>
              </div>
              <FormControl>
                <Textarea placeholder="" {...field} />
              </FormControl>
              <FormDescription>
                {t("leads.response.description")}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <h3 className="inline-block text-lg font-medium">
          <Icon
            className="inline-block mr-1"
            icon="ic:baseline-format-list-bulleted"
            width={20}
          />
          {t("fields")}
        </h3>

        <p className="text-sm text-muted-foreground !mt-0">
          {t("fields.description")}
        </p>

        {fieldsList.fields.map((field, index) => (
          <div key={field.id} className="bg-slate-50 rounded-lg shadow-md p-2">
            <FormField
              control={form.control}
              name={`fields.${index}.title`}
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-between items-center">
                    <FormLabel>{t("field.title")}</FormLabel>
                    <div
                      className="opacity-50 hover:opacity-100 cursor-pointer"
                      onClick={() => {
                        fieldsList.remove(index);
                      }}
                    >
                      <Icon
                        className="inline-block mr-1"
                        icon="ic:outline-close"
                        width={20}
                      />
                    </div>
                  </div>
                  <FormControl>
                    <Input placeholder="" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`fields.${index}.description`}
              render={({ field }) => (
                <FormItem className="mt-2">
                  <FormLabel>{t("field.description")}</FormLabel>
                  <FormControl>
                    <Input placeholder="" {...field} />
                  </FormControl>
                  <FormDescription>
                    {t("field.description.description")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        ))}

        <div>
          <Button
            variant="outline"
            type="button"
            onClick={() => {
              fieldsList.append({ title: "", description: "" });
            }}
          >
            {t('add')}
          </Button>
        </div>

        <div className="flex justify-end">
          <Button type="submit">Save</Button>
        </div>
      </form>
    </Form>
  );
}
